// Import Vue and other dependencies
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import vuetify from './plugins/vuetify';
import i18n, { loadActiveLocaleMessages, loadLegalMessages } from './plugins/i18n';
import router from './router'
import { useAuthStore } from '@/stores/authStore';
import MasonryWall from '@yeger/vue-masonry-wall'
import * as ErrorLogging from '@/error-logging.js';
import { fireFbViewContentFromPage } from '@/tracking.js';

// Component registry — every view is a lazy loader so Vite splits it into its
// own chunk. Only the component(s) whose tag is actually present on the page
// are fetched, instead of bundling all views into the entry chunk.
const components = {
  'dashboard': () => import('@/views/HomeView.vue'),
  'alldates': () => import('@/views/AllDatesView.vue'),
  'about-us': () => import('@/views/AboutUsView.vue'),
  'imprint': () => import('@/views/ImprintView.vue'),
  'terms': () => import('@/views/TermsView.vue'),
  'privacy': () => import('@/views/PrivacyView.vue'),
  'contact': () => import('@/views/ContactView.vue'),
  'account-overview': () => import('@/views/AccountOverview.vue'),
  'venue': () => import('@/views/LocationsView.vue'),
  'city-view': () => import('@/views/CityView.vue'),
  'artist': () => import('@/views/ArtistDetailsView.vue'),
  'event-category': () => import('@/views/EventCategoryView.vue'),
  'not-found': () => import('@/views/NotFoundView.vue'),
  'server-error': () => import('@/views/ServerErrorView.vue'),
  'route-example': () => import('./components/RouteExample.vue'),
  'vouchers': () => import('@/views/VouchersView.vue'),
  'event': () => import('@/views/EventView.vue'),
  'streaming': () => import('@/views/StreamingView.vue'),
  'partner-home': () => import('@/views/PartnerHomeView.vue'),
  'login': () => import('@/views/LoginView.vue'),
  'register': () => import('@/views/RegisterView.vue'),
  'forgot': () => import('@/views/ForgotPasswordView.vue'),
  'reset-password': () => import('@/views/ResetPasswordView.vue'),
  'account': () => import('@/views/AccountOverview.vue'),
  'hallplan': () => import('@/views/EventSeatsBooking.vue'),
  'checkout': () => import('@/views/CheckoutView.vue'),
  'date': () => import('@/views/EventTicketsBooking.vue'),
  'search': () => import('@/views/SearchResultsView.vue'),
  'dashboard-overview': () => import('@/views/DashboardView.vue'),
  'dashboard-dates': () => import('@/views/DashboardDatesView.vue'),
  'dashboard-venues': () => import('@/views/DashboardVenuesView.vue'),
  'dashboard-artists': () => import('@/views/DashboardArtistsView.vue'),
  'dashboard-events': () => import('@/views/DashboardEventsView.vue'),
  'dashboard-eventreviews': () => import('@/views/DashboardEventReviewsView.vue'),
  'dashboard-priceprofiles': () => import('@/views/DashboardPriceprofilesView.vue'),
  'dashboard-hallplans': () => import('@/views/DashboardHallplansView.vue'),
  'dashboard-hallplan-editor': () => import('@/views/DashboardHallplanEditorView.vue'),
  'dashboard-hallplan-upload': () => import('@/views/DashboardHallplanUploadView.vue'),
  'dashboard-seasontickets': () => import('@/views/DashboardSeasonticketsView.vue'),
  'dashboard-customers': () => import('@/views/DashboardCustomersView.vue'),
  'dashboard-customeraddress': () => import('@/views/DashboardCustomerAddressView.vue'),
  'dashboard-orders': () => import('@/views/DashboardOrdersView.vue'),
  'dashboard-boxoffice': () => import('@/views/BoxOfficeView.vue'),
  'dashboard-discounts': () => import('@/views/DashboardDiscountsView.vue'),
  'dashboard-campaigns': () => import('@/views/DashboardCampaignsView.vue'),
  'dashboard-qr-codes': () => import('@/views/DashboardQrCodesView.vue'),
  'dashboard-system': () => import('@/views/DashboardSystemView.vue'),
  'dashboard-articles': () => import('@/views/DashboardArticlesView.vue'),
  'dashboard-shipping-methods': () => import('@/views/DashboardShippingMethodsView.vue'),
  'dashboard-reservation': () => import('@/views/DashboardReservationView.vue'),
  'dashboard-vouchers': () => import('@/views/DashboardVouchersView.vue'),
  'dashboard-livescan': () => import('@/views/DashboardLivescanView.vue'),
  'dashboard-bookinglist': () => import('@/views/DashboardBookinglistView.vue'),
  'dashboard-invoices': () => import('@/views/DashboardInvoicesView.vue'),
  'dashboard-webhooks': () => import('@/views/DashboardWebhooksView.vue'),
  'dashboard-vouchersupport': () => import('@/views/DashboardVouchersupportView.vue'),
  'dashboard-falseblockedseats': () => import('@/views/DashboardFalseblockedseatsView.vue'),
  'dashboard-filemanager': () => import('@/views/DashboardFilemanagerView.vue'),
  'dashboard-activecarts': () => import('@/views/DashboardActivecartsView.vue'),
  'dashboard-reportevents': () => import('@/views/DashboardReporteventsView.vue'),
  'dashboard-shipping': () => import('@/views/DashboardShippingView.vue'),
  'dashboard-directdebits': () => import('@/views/DashboardDirectdebitsView.vue'),
  'dashboard-accounting': () => import('@/views/DashboardAccountingView.vue'),
  'dashboard-tablereservation': () => import('@/views/DashboardTableReservationView.vue'),
  'dashboard-bookingform': () => import('@/views/DashboardBookingFormView.vue'),
  'dashboard-report-bookingform': () => import('@/views/DashboardReportBookingFormView.vue'),
  'dashboard-revenues-partnershop': () => import('@/views/DashboardRevenuesPartnershopView.vue'),
  'dashboard-partner-requests': () => import('@/views/DashboardPartnerRequestsView.vue'),
  'dashboard-partners': () => import('@/views/DashboardPartnersView.vue'),
  'dashboard-partner-detail': () => import('@/views/DashboardPartnerDetailView.vue'),
  'dashboard-partner-profile': () => import('@/views/DashboardPartnerProfileView.vue'),
  'dashboard-streamings': () => import('@/views/DashboardStreamingsView.vue'),
  'opc-checkout': () => import('@/views/OpcView.vue'),
  'articles': () => import('@/views/ShopArticlesView.vue'),
  'help': () => import('@/views/HelpView.vue'),
  'jobs': () => import('@/views/JobsView.vue'),
  'artists': () => import('@/views/AllArtistsView.vue'),
  'events': () => import('@/views/AllEventsView.vue'),
  'partner-create': () => import('@/views/PartnerCreateView.vue'),
  'partner-onboarding': () => import('@/views/PartnerOnboardingView.vue'),
  'partner-ticket-editor': () => import('@/views/PartnerTicketEditorView.vue'),
};

// After a new frontend deploy the asset hashes on the CDN change, so an old
// entry chunk still open in a tab references CSS/JS chunks that 404. Vite's
// __vitePreload helper fires a `vite:preloadError` event in that case. Reload
// once to pick up the fresh manifest. The sessionStorage guard stores the time
// of the last reload so a genuinely missing asset can't trigger an endless
// reload loop, while a later deploy (outside the window) can still self-recover.
const PRELOAD_RELOAD_KEY = 'vite-preload-reloaded-at';
const PRELOAD_RELOAD_WINDOW_MS = 10000;
const PRELOAD_LOGGED_KEY = 'vite-preload-logged';

function logPreloadError(event) {
  // A stuck tab can fire this repeatedly within the reload window; one report
  // per session is enough to surface a genuinely broken deploy without flooding.
  try {
    if (sessionStorage.getItem(PRELOAD_LOGGED_KEY)) {
      return;
    }
    sessionStorage.setItem(PRELOAD_LOGGED_KEY, '1');
  } catch (storageError) {
    // sessionStorage unavailable: fall through and log once (no dedupe possible).
  }

  // Don't reuse the original "Unable to preload…" message — it matches the
  // excludedPatterns in error-logging and would be dropped. Use a distinct
  // message so a genuinely broken build stays visible, with the original in meta.
  // info, not warning: this is deploy-correlated and self-heals on next navigation.
  ErrorLogging.log('vite:preloadError persisted after reload', {
    level: 'info',
    meta: {
      source: 'vite:preloadError',
      originalMessage: (event.payload && event.payload.message) || null,
    },
  });
}

function extractAssetUrl(message) {
  const match = message && message.match(/https?:\/\/\S+/);
  return match ? match[0] : null;
}

// Reload once to pick up a fresh manifest, or log if a prior reload already
// failed to resolve it. sessionStorage can throw in Safari Private Mode /
// sandboxed embeds — without a cross-reload guard we can't prevent a loop, so
// we skip the reload and only log it as a possibly broken build.
function reloadOrLogPersisted(event) {
  let lastReloadAt = 0;
  let storageAvailable = true;
  try {
    lastReloadAt = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
  } catch (storageError) {
    storageAvailable = false;
  }

  // Already reloaded within the window (fresh manifest loaded yet the asset
  // still 404s → genuinely broken deploy) or we can't guard against a loop.
  if (!storageAvailable || Date.now() - lastReloadAt < PRELOAD_RELOAD_WINDOW_MS) {
    logPreloadError(event);
    return;
  }

  try {
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
  } catch (storageError) {
    logPreloadError(event);
    return;
  }
  window.location.reload();
}

// A failing route fires one vite:preloadError per chunk, so a single broken load
// triggers a burst of N parallel handlers. Without a guard they race: one wins
// the reload and the rest see the freshly-set timestamp and each log "persisted",
// while also flooding the CDN with N concurrent no-store probes. Handle only the
// first of a burst; the flag resets once that handler settles.
let preloadHandlingInProgress = false;

// Most preloadErrors are transient: a flaky-network blip or a false-positive
// stylesheet `error` event while the asset is in fact served fine. Reloading
// for those only discards client state and floods the logs, so probe the real
// HTTP status of the failed asset first and only act on a genuine 4xx/5xx (a
// stale post-deploy manifest). A reachable asset or a network failure (offline,
// flaky mobile, missing CORS) is swallowed silently.
async function handlePreloadError(event) {
  if (preloadHandlingInProgress) {
    return;
  }
  preloadHandlingInProgress = true;

  try {
    const failedUrl = extractAssetUrl((event.payload && event.payload.message) || '');
    if (!failedUrl) {
      return;
    }

    let status;
    try {
      status = (await fetch(failedUrl, { cache: 'no-store' })).status;
    } catch (fetchError) {
      return;
    }

    if (status >= 200 && status < 400) {
      return;
    }

    reloadOrLogPersisted(event);
  } finally {
    preloadHandlingInProgress = false;
  }
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  handlePreloadError(event);
});

// The component/i18n loaders below are bare `await import()` calls, so they are
// NOT wrapped by Vite's __vitePreload helper and never fire `vite:preloadError`
// on a stale post-deploy chunk. Worse, mobile Safari sometimes resolves a failed
// module import with `undefined` instead of rejecting, so the subsequent
// `.default` access throws "undefined is not an object (evaluating
// '<var>.default')" as an unhandled rejection (#5256). Route both cases into the
// same reload-once path used for preloadError (#5124) so the fresh manifest is
// picked up; the sessionStorage guard is shared, so this can't loop.
function handleChunkLoadError(label, error) {
  const detail = error && error.message ? ` (${error.message})` : '';
  reloadOrLogPersisted({
    payload: { message: `dynamic import failed after deploy: ${label}${detail}` },
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  ErrorLogging.init();

  // Mirror a server-rendered ViewContent CAPI event into the first-party FB
  // browser pixel (same event_id) so Meta can deduplicate server + browser.
  fireFbViewContentFromPage();

  const pinia = createPinia();

  const authStore = useAuthStore(pinia);
  await authStore.init();

  // Load the active locale's translations before mounting so the first render
  // already has its messages (only this language is fetched, see plugins/i18n).
  try {
    await loadActiveLocaleMessages();
  } catch (error) {
    handleChunkLoadError('locale messages', error);
  }

  // Legal pages are mounted as their own component tags (not via the router),
  // so their split-out translations must be merged here before mounting.
  for (const legalPage of ['imprint', 'terms', 'privacy']) {
    if (document.getElementsByTagName(legalPage).length > 0) {
      try {
        await loadLegalMessages(legalPage);
      } catch (error) {
        handleChunkLoadError(`legal messages: ${legalPage}`, error);
      }
    }
  }

  // Check for component tags in the DOM
  for (const [name, loader] of Object.entries(components)) {
    const elements = document.getElementsByTagName(name);

    if (elements.length === 0) continue;

    // Lazily fetch only the component(s) actually present on this page. A stale
    // post-deploy chunk either rejects or (mobile Safari) resolves `undefined`;
    // both are routed into the reload-once path so the page self-heals (#5256).
    let module;
    try {
      module = await loader();
    } catch (error) {
      handleChunkLoadError(name, error);
      continue;
    }
    if (module == null) {
      handleChunkLoadError(name, null);
      continue;
    }
    const component = module.default ?? module;

    // For each instance of the component tag
    Array.from(elements).forEach((el, index) => {
      // Create a unique ID for this component instance
      const mountId = `vue-${name}-${index}`;

      // Create a div with the mount ID
      const mountPoint = document.createElement('div');
      mountPoint.id = mountId;

      // Replace the component tag with the mount point
      el.parentNode.replaceChild(mountPoint, el);

      // Create and mount the Vue app
      const app = createApp(component, {
        // Pass any props from data attributes
        ...el.dataset,
        // Pass global data if available
        ...(window.appData?.[name] || {})
      });

      // Enable devtools in development mode
      if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
        app.config.devtools = true;
      }

      // Make session ID available globally
      app.config.globalProperties.$session = window.Laravel?.session || null;
      app.config.globalProperties.$route = route;

      app.use(vuetify)
      app.use(i18n)
      app.use(router)
      app.use(MasonryWall)
      app.use(pinia);
      app.mount(`#${mountId}`);
    });
  }
});
